/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useMemo } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { useSearchFormRenderedDataStore } from '../../modules/store/searchFormRenderedDataStore';
import { useSearchResultsRenderedDataStore } from '../../modules/store/searchResultsRenderedDataStore';

interface UseConditionalRenderedDataOptions {
  /**
   * Specify which component is using this hook to determine the correct feature flag
   * 'adf' will check EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (for AvailabilityCalendar and CalendarWrapper)
   * 'serp' will check EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (for all other components)
   */
  flagType?: 'adf' | 'serp';
}

/**
 * Hook that conditionally returns SearchForm renderedData from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalSearchFormRenderedData = (
  renderedDataContext: React.Context<any>,
  options: UseConditionalRenderedDataOptions = { flagType: 'serp' }
) => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context
  const contextData = useContext(renderedDataContext);

  // Get Zustand store functions and state
  const zustandStore = useSearchFormRenderedDataStore();
  const {
    initializeSearchFormRenderedData,
    resetSearchFormRenderedData,
    setNightCount,
    setMonthSelected,
    setYearSelected,
    setTabSelected,
    ...zustandState
  } = zustandStore;

  if (shouldUseZustand) {
    // Return ONLY Zustand store data - NO context fallbacks
    return {
      // Pure Zustand store values
      nightCount: zustandState?.nightCount || 0,
      monthSelected: zustandState?.monthSelected || 0,
      yearSelected: zustandState?.yearSelected || 0,
      tabSelected: zustandState?.tabSelected || '',

      // Action methods from store only
      setNightCount: setNightCount || (() => {}),
      setMonthSelected: setMonthSelected || (() => {}),
      setYearSelected: setYearSelected || (() => {}),
      setTabSelected: setTabSelected || (() => {}),

      // Store management methods
      initializeSearchFormRenderedData: initializeSearchFormRenderedData || (() => {}),
      resetSearchFormRenderedData: resetSearchFormRenderedData || (() => {}),
      isInitialState: zustandStore.isInitialState || (() => true),
    };
  } else {
    // Return ONLY React Context data - pure context path
    return contextData;
  }
};

/**
 * Hook that conditionally returns SearchResults renderedData from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalSearchResultsRenderedData = (
  renderedDataContext: React.Context<any>,
  options: UseConditionalRenderedDataOptions = { flagType: 'serp' }
) => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context
  const contextData = useContext(renderedDataContext);

  // Get Zustand store functions and state
  const zustandStore = useSearchResultsRenderedDataStore();

  const {
    initializeSearchResultsRenderedData,
    resetSearchResultsRenderedData,
    setISMapViewEnabled,
    setRecentlyViewed,
    setFeatureSelected,
    setIsFlexibleDateSearch,
    setIsSearchClicked,
    setIsStateOrCountry,
    setContinueCtaProperty,
    setSessionObject,
    setRevisionToken,
    setSavedProperties,
    setFormValues,
    setIsSpecialRateSearch,
    setIsAutoSuggestSelectedSearch,
    setIsRecentViewSelectedSearch,
    setIsErrorState,
    setIsLastCardProcessed,
    setImageURL,
    setIsTaxesandFeesIncludedFromPopup,
    ...zustandState
  } = zustandStore;

  // Deep clone jsonContent to produce mutable objects for downstream consumers
  // (Immer freezes store state, but components like BrandFilters mutate brand.disable in-place)
  // useMemo ensures the clone only runs when the source reference changes
  const mutableJsonContent = useMemo(
    () => (zustandState?.jsonContent ? JSON.parse(JSON.stringify(zustandState.jsonContent)) : {}),
    [zustandState?.jsonContent]
  );

  if (shouldUseZustand) {
    // Return ONLY Zustand store data - NO context fallbacks
    return {
      // Memoized deep clone of jsonContent to prevent Immer immutability errors
      jsonContent: mutableJsonContent,
      configData: zustandState?.configData || {},
      isMapViewEnabled: zustandState?.isMapViewEnabled ?? false,
      recentlyViewed: zustandState?.recentlyViewed || [],
      featureSelected: zustandState?.featureSelected ?? false,
      isFlexibleDateSearch: zustandState?.isFlexibleDateSearch ?? false,
      isSearchClicked: zustandState?.isSearchClicked ?? false,
      isStateOrCountry: zustandState?.isStateOrCountry ?? false,
      continueCtaProperty: zustandState?.continueCtaProperty || '',
      sessionObject: zustandState?.sessionObject || { consumerID: '', accessToken: '', sessionID: '' },
      revisionToken: zustandState?.revisionToken || '',
      savedProperties: zustandState?.savedProperties || [],
      formValues: zustandState?.formValues || {},
      isSpecialRateSearch: zustandState?.isSpecialRateSearch ?? false,
      isAutoSuggestSelectedSearch: zustandState?.isAutoSuggestSelectedSearch ?? false,
      isRecentViewSelectedSearch: zustandState?.isRecentViewSelectedSearch ?? false,
      isErrorState: zustandState?.isErrorState ?? true,
      isLastCardProcessed: zustandState?.isLastCardProcessed ?? false,
      imageURL: zustandState?.imageURL || '',
      isTaxesandFeesIncludedFromPopup: zustandState?.isTaxesandFeesIncludedFromPopup ?? false,
      isStaticMap: zustandState?.isStaticMap ?? false,
      isMapLoading: zustandState?.isMapLoading ?? false,

      // Action methods from store only
      setISMapViewEnabled: setISMapViewEnabled || (() => {}),
      setRecentlyViewed: setRecentlyViewed || (() => {}),
      setFeatureSelected: setFeatureSelected || (() => {}),
      setIsFlexibleDateSearch: setIsFlexibleDateSearch || (() => {}),
      setIsSearchClicked: setIsSearchClicked || (() => {}),
      setIsStateOrCountry: setIsStateOrCountry || (() => {}),
      setContinueCtaProperty: setContinueCtaProperty || (() => {}),
      setSessionObject: setSessionObject || (() => {}),
      setRevisionToken: setRevisionToken || (() => {}),
      setSavedProperties: setSavedProperties || (() => {}),
      setFormValues: setFormValues || (() => {}),
      setIsSpecialRateSearch: setIsSpecialRateSearch || (() => {}),
      setIsAutoSuggestSelectedSearch: setIsAutoSuggestSelectedSearch || (() => {}),
      setIsRecentViewSelectedSearch: setIsRecentViewSelectedSearch || (() => {}),
      setIsErrorState: setIsErrorState || (() => {}),
      setIsLastCardProcessed: setIsLastCardProcessed || (() => {}),
      setImageURL: setImageURL || (() => {}),
      setIsTaxesandFeesIncludedFromPopup: setIsTaxesandFeesIncludedFromPopup || (() => {}),

      // Store management methods
      initializeSearchResultsRenderedData: initializeSearchResultsRenderedData || (() => {}),
      resetSearchResultsRenderedData: resetSearchResultsRenderedData || (() => {}),
    };
  } else {
    // Return ONLY React Context data - pure context path
    return contextData;
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SearchForm renderedData
 */
export const useConditionalSearchFormRenderedDataADF = (renderedDataContext: React.Context<any>) => {
  return useConditionalSearchFormRenderedData(renderedDataContext, { flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SearchForm renderedData
 */
export const useConditionalSearchFormRenderedDataSERP = (renderedDataContext: React.Context<any>) => {
  return useConditionalSearchFormRenderedData(renderedDataContext, { flagType: 'serp' });
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SearchResults renderedData
 */
export const useConditionalSearchResultsRenderedDataADF = (renderedDataContext: React.Context<any>) => {
  return useConditionalSearchResultsRenderedData(renderedDataContext, { flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SearchResults renderedData
 */
export const useConditionalSearchResultsRenderedDataSERP = (renderedDataContext: React.Context<any>) => {
  return useConditionalSearchResultsRenderedData(renderedDataContext, { flagType: 'serp' });
};
